import { intervalToDuration } from 'date-fns';

/**
 * Strips a Date down to its UTC year/month/day (midnight UTC),
 * so timezone-sensitive day arithmetic is consistent regardless
 * of the browser's locale.
 */
export const toUTCDate = (date) =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

/**
 * Parse an API date string as UTC. The API returns UTC timestamps but omits
 * the trailing 'Z' (e.g. "2026-06-18 19:00:00"), which new Date() would read
 * as local time. This normalises the string so the instant is correct for
 * every user regardless of their browser timezone.
 */
export const toUtcParsedDate = (datestr) =>
    new Date(typeof datestr === 'string' && !datestr.endsWith('Z') ? datestr.replace(' ', 'T') + 'Z' : datestr);

export const dateFormatterRelative = (datestr) => {
    const date = toUtcParsedDate(datestr);
    const ONE_DAY = 1000 * 60 * 60 * 24;
    const differenceMs = Math.abs(new Date().valueOf() - date);
    const daysDifference = Math.round(differenceMs / ONE_DAY);

    if (daysDifference === 0) return 'today';
    if (daysDifference === 1) return 'yesterday';
    return daysDifference + ' days ago';
};

export const getDaySuffix = (day) => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
    }
};

export const formatFixtureDate = (dateString, fullWeekday = false, withSuffix = false, utc = false) => {
    const date = new Date(dateString);
    const timeZone = utc ? "UTC" : undefined;
    const weekday = date.toLocaleDateString(undefined, { weekday: fullWeekday ? "long" : "short", timeZone });
    const day = date.toLocaleDateString(undefined, { day: "numeric", timeZone });
    const month = date.toLocaleDateString(undefined, { month: "short", timeZone });
    const dayStr = withSuffix ? `${day}${getDaySuffix(Number(day))}` : day;
    return `${weekday}, ${dayStr} ${month}`;
};

// "DD MMM YYYY" (e.g. "02 Sep 2026") — zero-padded day, short month, full year.
export const formatDateDDMMMYYYY = (dateString) => {
    const date = new Date(dateString);
    const day = date.toLocaleDateString(undefined, { day: "2-digit" });
    const month = date.toLocaleDateString(undefined, { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

// "DD MMMM YYYY" (e.g. "02 September 2026") — zero-padded day, full month, full year.
export const formatDateDDMMMMYYYY = (dateString) => {
    const date = new Date(dateString);
    const day = date.toLocaleDateString(undefined, { day: "2-digit" });
    const month = date.toLocaleDateString(undefined, { month: "long" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

export const formatFixtureDateYear = (dateString) => {
    const date = new Date(dateString);
    const weekday = date.toLocaleDateString(undefined, { weekday: "short" });
    const day = date.toLocaleDateString(undefined, { day: "numeric" });
    const month = date.toLocaleDateString(undefined, { month: "short" });
    const year = date.getFullYear();
    return `${weekday}, ${day} ${month} ${year}`;
};

export const formatTime24 = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
};

export const formatTime12 = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true }).replace(/\s/g, '');
};

/**
 * Returns the browser-local timezone abbreviation (e.g. "BST", "GMT",
 * "EDT", "GMT+9") for the given date. DST-aware: a summer fixture will
 * return "BST" and a winter one "GMT" for a London user.
 */
export const getTimezoneAbbreviation = (dateString) => {
    const date = new Date(dateString)
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(date);
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
};

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

// 2026/27 Premier League season starts 21 Aug 2026. Until 2 weeks of it have
// been played, pin the range to a fixed preseason window instead of the
// normal rolling one, so there's always fixture content to show. This is a
// one-off seasonal fix — remove or update once the 2026/27 season is under way.
const PL_2026_27_SEASON_START = new Date(Date.UTC(2026, 7, 21));
const PL_2026_27_TWO_WEEKS_IN = new Date(PL_2026_27_SEASON_START.getTime() + TWO_WEEKS_MS);
const PRESEASON_FROM = new Date(Date.UTC(2026, 6, 9));

// Matches resume 6 Oct 2026 after the Oct 2026 international break. Floor dateTo at two
// weeks after that so the window doesn't shrink to nothing during the break itself. Once
// "now" passes the break, now + 2 weeks naturally exceeds this and the max() below falls
// back to the normal rolling window on its own — no expiry date to maintain.
const INTERNATIONAL_BREAK_RESUMES = new Date(Date.UTC(2026, 9, 6));
const INTERNATIONAL_BREAK_TWO_WEEKS_AFTER = new Date(INTERNATIONAL_BREAK_RESUMES.getTime() + TWO_WEEKS_MS);

/**
 * Builds the date_from/date_to query strings used by the fixtures API,
 * anchored to UTC midnight so results are consistent regardless of the
 * browser's timezone. Normally a rolling 2-week-back/2-week-ahead window;
 * see PL_2026_27_* above for the preseason override and
 * INTERNATIONAL_BREAK_* above for the break-window floor on dateTo.
 */
export const getFixtureDateRange = () => {
    const now = toUTCDate(new Date());

    const fromDate = now < PL_2026_27_TWO_WEEKS_IN
        ? PRESEASON_FROM
        : toUTCDate(new Date(now.getTime() - TWO_WEEKS_MS));

    const rollingToDate = new Date(now.getTime() + TWO_WEEKS_MS);
    const toDate = now < PL_2026_27_SEASON_START
        ? PL_2026_27_TWO_WEEKS_IN
        : toUTCDate(new Date(Math.max(rollingToDate.getTime(), INTERNATIONAL_BREAK_TWO_WEEKS_AFTER.getTime())));

    return {
        dateFrom: fromDate.toISOString().slice(0, 10),
        dateTo: toDate.toISOString().slice(0, 10)
    };
};

export const getSeasonYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const nextYear = (year + 1).toString().slice(-2);
    return `${year}/${nextYear}`;
};

export const durationFormatter = (timeStamp, { withAgo = true } = {}) => {
    const diff = Math.abs(new Date().valueOf() - new Date(timeStamp).valueOf());
    const duration = intervalToDuration({ start: 0, end: diff });

    let value;
    if (duration.years) value = `${duration.years}y`;
    else if (duration.months) value = `${duration.months}mo`;
    else if (duration.days >= 7) value = `${Math.floor(duration.days / 7)}wk`;
    else if (duration.days) value = `${duration.days}d`;
    else if (duration.hours) value = `${duration.hours}h`;
    else if (duration.minutes) value = `${duration.minutes}m`;
    else value = `${duration.seconds ?? 0}s`;

    return withAgo ? `${value} ago` : value;
};

// Buckets a fixture into one of: past | yesterday | today | tomorrow | upcoming
// (by local calendar day). Shared by the fixtures accordion + tab grouping.
export const bucketForFixture = (matchDate) => {
    const d = new Date(matchDate.replace(" ", "T"));

    const startOfDay = (date) => {
        const x = new Date(date);
        x.setHours(0, 0, 0, 0);
        return x;
    };

    const today = startOfDay(new Date());
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);

    const fixtureDay = startOfDay(d);

    if (fixtureDay.getTime() < yesterday.getTime()) return "past";
    if (fixtureDay.getTime() === yesterday.getTime()) return "yesterday";
    if (fixtureDay.getTime() === today.getTime()) return "today";
    if (fixtureDay.getTime() === tomorrow.getTime()) return "tomorrow";
    return "upcoming";
};
